import { ActionStatus } from '@common/constants/shared';
import { fetchSignedFileUrl } from '@common/services/featherIO';
import { IAction } from '@common/types/';
import { MetadataAction } from '@common/types/action';
import { get } from 'lodash';
import { Platform } from 'react-native';
import { ActionResponse } from '../excuteAction';

const downloadFileWeb = async ({
  fileExt,
  fileName,
  url,
}: {
  url: string;
  fileExt: string;
  fileName: string;
}) => {
  const filename = `${fileName}${fileExt ? `.${fileExt}` : ''}`;

  let response: Response;
  try {
    response = await fetch(url);
  } catch (error) {
    // The request never completed - most often CORS or the network. Handing the
    // URL to the browser still gives the user a way through.
    console.error(error);
    window.open(url, '_blank');
    return;
  }

  // An expired pre-signed URL answers 403 with an XML <Error> body. Without
  // this check that body was saved as the file, so the download "succeeded"
  // and produced a document full of AccessDenied markup.
  if (!response.ok) {
    throw new Error(`Download failed with HTTP ${response.status}`);
  }

  const blob = await response.blob();
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(link.href);
};

export const downloadFile = async (
  action: Pick<IAction, 'response'>,
  metadata?: MetadataAction
): Promise<ActionResponse> => {
  const url = get(action, 'response.url', '');
  const fileExt = get(action, 'response.fileExt', '');
  const fileName =
    get(action, 'response.fileName', null) || Date.now().toString();

  if (typeof url !== 'string' || !url) {
    return {
      status: ActionStatus.FAILED,
    };
  }

  try {
    if (Platform.OS === 'web') {
      // The stored URL is only good for five minutes, and the one we hold may
      // have been minted long before the user clicked. Sign again at click
      // time; anything that is not ours to sign comes back untouched.
      const signedUrl = await fetchSignedFileUrl(url);
      await downloadFileWeb({ url: signedUrl, fileExt, fileName });
      return {
        status: ActionStatus.SUCCESS,
      };
    }
    return {
      status: ActionStatus.FAILED,
      message: 'This device is not supported yet!',
    };
  } catch (error: any) {
    return {
      status: ActionStatus.FAILED,
      message: error.message,
    };
  }
};
